/**
 * Viewport-aware placement for a dropdown menu that is rendered in a portal
 * (see `AnchoredMenu`) rather than as an absolutely-positioned child of its
 * trigger.
 *
 * Why a portal at all: the list pages wrap their table in a
 * `rounded-lg overflow-hidden` container so the header background respects the
 * rounded corner. That `overflow: hidden` also clips absolutely-positioned
 * descendants, so a row menu on the LAST row was cut off at the table's bottom
 * edge and effectively invisible — no amount of page scrolling helped, because
 * the menu was clipped rather than off-screen.
 *
 * Pure + framework-free so it can be unit-tested without a DOM (the repo has no
 * jsdom — see docs/lessons.md Lesson #49).
 */

/** Gap between the trigger and the menu, in px. */
export const MENU_GAP = 4;

/** Minimum breathing room between the menu and any viewport edge, in px. */
export const MENU_VIEWPORT_MARGIN = 8;

/** The trigger's viewport-relative box (a `DOMRect` satisfies this). */
export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface MenuPosition {
  /** `position: fixed` coordinates, viewport-relative. */
  top: number;
  left: number;
  placement: 'above' | 'below';
  /** Cap so an over-tall menu scrolls internally instead of overflowing. */
  maxHeight: number;
}

/**
 * Place `menu` relative to `anchor`, preferring below and flipping above when
 * the menu would overflow the bottom of the viewport. When neither side fits,
 * the roomier side wins and `maxHeight` constrains the menu so it scrolls.
 *
 * Horizontally the menu is right-aligned to the trigger (matching the previous
 * `right-0` behaviour) and clamped to the viewport.
 */
export const computeMenuPosition = (
  anchor: AnchorRect,
  menu: Size,
  viewport: Size,
  gap: number = MENU_GAP,
): MenuPosition => {
  const margin = MENU_VIEWPORT_MARGIN;

  const roomBelow = viewport.height - anchor.bottom - gap - margin;
  const roomAbove = anchor.top - gap - margin;

  const fitsBelow = menu.height <= roomBelow;
  const fitsAbove = menu.height <= roomAbove;

  // Prefer below; flip up only when below genuinely doesn't fit and above does.
  // If neither fits, take whichever side has more room and let maxHeight scroll.
  const placement: 'above' | 'below' =
    fitsBelow || (!fitsAbove && roomBelow >= roomAbove) ? 'below' : 'above';

  const available = Math.max(0, placement === 'below' ? roomBelow : roomAbove);
  const maxHeight = Math.min(menu.height, available);

  const rawTop =
    placement === 'below'
      ? anchor.bottom + gap
      : anchor.top - gap - Math.min(menu.height, maxHeight);

  // Never let the menu start above the viewport top.
  const top = Math.max(margin, rawTop);

  // Right-align to the trigger, then clamp into the viewport. A menu wider than
  // the viewport clamps to 0 rather than a negative offset.
  const maxLeft = viewport.width - menu.width - margin;
  const left = Math.max(
    0,
    Math.min(Math.max(margin, anchor.right - menu.width), Math.max(0, maxLeft)),
  );

  return { top, left, placement, maxHeight };
};
